import { defineMeta } from '@/registry/types';

export const surfaceMeta = defineMeta({
  name: 'Surface',
  level: 'atom',
  category: 'layout',
  classification: 'custom',
  description:
    'A background that resolves its own colour from how deeply it is nested, and publishes --ds-* custom properties so descendants stay consistent with it.',
  baseLibrary: 'custom',
  a2uiEquivalent: 'Container',
  importPath: '@/atoms/Surface',
  mountsSurface: 'inline',
  optionalProps: ['rung', 'component', 'sx', 'children'],
  supportedStates: ['page', 'primary', 'widget', 'surface', 'raised', 'overlay'],
  designTokens: ['colors', 'surfaceScopes'],
  accessibility: [
    'Purely presentational: renders a div with no role, so it does not affect the accessibility tree.',
    'Quiet text switches from `text.subtle` to `text.tertiary` above `widget`, which is what keeps de-emphasised text at 4.5:1 on higher rungs.',
  ],
  usageExamples: [
    '<Surface>{/* app shell — resolves to page */}</Surface>',
    '<Surface><Surface>{/* a card on the page body — resolves to widget */}</Surface></Surface>',
    '<Surface rung="raised">{/* portalled tooltip, cannot inherit */}</Surface>',
  ],
  rules: [
    'Let the rung derive from nesting. Wrap content and the correct colour follows.',
    'Set `rung` explicitly only for portalled content — tooltips, menus, dialogs — which is reparented to document.body and cannot inherit CSS variables. Use `usePortalRung()` to compute the value.',
    'Read `var(--ds-field)` for an input background so it recesses one rung below its host, and `var(--ds-text-quiet)` for de-emphasised text so it stays legible as the ladder lightens.',
    'Three levels of nesting is the maximum. If you need a fourth, one of the outer three should be a plain container instead.',
  ],
  antiPatterns: [
    'Do not hardcode a background hex on a container; that is the drift this component exists to remove.',
    'Do not nest more than three deep. A fourth level is painted at its parent rung and logs an error in development, so the nesting has no visual effect.',
    'Do not use `rung` to skip up the ladder for emphasis. Rungs encode containment, not importance.',
  ],
  llmSafe: true,
  whenToUse: [
    'Any container that needs a background: a card, a panel, a dialog body, a side drawer',
    'Wrapping portalled overlay content so it declares a rung the DOM cannot give it',
  ],
  whenNotToUse: [
    'A container that needs no background of its own — use Box or Stack, and let the surface show through',
    'Conveying emphasis or severity, which is what Chip, Alert and the severity tokens are for',
  ],
});

export default surfaceMeta;
